import React from 'react';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';
import Slider from '@material-ui/core/Slider';
import { StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import { assetLink } from '../assets';
import { useSelector } from 'react-redux';
import config from '../../../config';

export const e4Player = ({ asset, progress, allow_FF, video, onPlayerProgress, onPlayerEnd, setHeight }) => {
    const player = React.useRef(null);
    const playerWrapper = React.useRef(null);
    //const [url, setUrl] = React.useState('');
    const [playing, setPlaying] = React.useState(false);
    const [total, setTotal] = React.useState(0);
    const [maxDuration, setMaxDuration] = React.useState(0);
    const [current, setCurrent] = React.useState(0);
    const [ready, setReady] = React.useState(false);
    const [hasInfinity, setHasInfinity] = React.useState(false);

    const user = useSelector(state => state.user.user);

    const disabled = current === maxDuration;

    const videoConfig = setHeight
        ? {
              youtube: {
                  playerVars: { showinfo: 0, controls: 0, height: '400' },
              },
          }
        : {};

    const onSeek = secs => {
        let newCurrent = current + secs;
        newCurrent = newCurrent < 0 ? 0 : newCurrent;
        newCurrent = newCurrent > maxDuration ? maxDuration : newCurrent;
        setCurrent(newCurrent);
        player.current.seekTo(newCurrent);
    };

    const playerReady = state => {
        state.getDuration();
    };

    const onDuration = i => {
        if (i === Infinity) {
            setHasInfinity(true);
        } else {
            const t = Math.floor(i);
            const initCurrent = Math.ceil((t * progress) / 100);
            setMaxDuration(allow_FF ? t : initCurrent);
            setCurrent(initCurrent);
            setTotal(t);
            setReady(true);
            const seekTo = initCurrent === t ? 0 : initCurrent;
            seekTo > 0 && player.current.seekTo(seekTo);
        }
    };

    const onProgress = state => {
        const oldMax = maxDuration;
        const current = Math.ceil(state.playedSeconds);
        const newMax = oldMax > current ? oldMax : current;
        setCurrent(current);
        setMaxDuration(newMax);
        onPlayerProgress?.((newMax / total) * 100);
    };

    const st = (path, ...rest) => SystemText(`General/Controls/Player/${path}`, ...rest);

    const onEnded = () => {
        onPlayerEnd?.();
    };

    let url = assetLink(asset);
    url = video && asset.source === 'onedrive' ? `${url}${url.indexOf('?') < 0 ? '?' : '&'}download=1` : url;

    if (video && asset.source === 'google') {
        const domain = user.companySettings?.googleDomain;
        url = domain
            ? `https://drive.google.com/uc?export=download&id=${asset.internal_Id}&confirm=t`
            : `https://www.googleapis.com/drive/v3/files/${asset.internal_Id}?alt=media&key=${config.google.shareKey}`;
    }

    if (hasInfinity)
        return (
            <div>
                <video onTimeUpdate={null} className="video-player" preload="metadata" controls>
                    <source src={url} />
                    {st('NotSupported')}
                </video>
            </div>
        );

    return (
        <div>
            <div ref={playerWrapper}>
                <div
                    style={
                        playerWrapper.current && setHeight
                            ? {
                                  maxWidth: `${
                                      (playerWrapper.current.clientWidth * (window.innerHeight - 150)) /
                                      playerWrapper.current.clientHeight
                                  }px`,
                              }
                            : null
                    }
                    className={screenfull.isFullscreen ? 'fullscreen' : ''}
                >
                    <div className="player-wrapper">
                        <ReactPlayer
                            className="react-player"
                            height={null}
                            ref={player}
                            width="100%"
                            url={url}
                            playing={playing}
                            onReady={playerReady}
                            onDuration={onDuration}
                            onProgress={onProgress}
                            onEnded={onEnded}
                            played={total}
                            config={videoConfig}
                            onPlay={() => setPlaying(true)}
                            onPause={() => setPlaying(false)}
                        />
                        <div className="clearfix" />
                    </div>
                    <div>
                        <div className="mt10 mb5 text-center">
                            <div
                                className={`slider${screenfull.isFullscreen ? '-full' : ''}`}
                                style={{ width: '360px', display: 'inline-block', color: '#000' }}
                            >
                                <Slider
                                    min={0}
                                    max={total}
                                    value={current}
                                    onChange={(e, current) => {
                                        const origPlaying = playing;
                                        const setTo = current > maxDuration ? maxDuration : current;
                                        setPlaying(false);
                                        player.current.seekTo(setTo);
                                        setTimeout(() => {
                                            setCurrent(setTo);
                                            setPlaying(origPlaying);
                                        }, 100);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="btn-row text-center">
                            <button
                                data-testid="button-start"
                                disabled={!ready}
                                title={st('Buttons/Start')}
                                className="btn btn-lg"
                                onClick={() => player?.current.seekTo(0)}
                            >
                                <i className="fa fa-fast-backward" />
                            </button>
                            <button
                                data-testid="button-rewind"
                                disabled={!ready}
                                title={st('Buttons/Rewind10')}
                                className="btn btn-lg"
                                onClick={() => onSeek(-10)}
                            >
                                <i className="fa fa-backward" />
                            </button>
                            <button
                                disabled={!ready}
                                data-testid="button-play"
                                title={st(`Buttons/${playing ? 'Pause' : 'Play'}`)}
                                className="btn btn-lg"
                                onClick={() => setPlaying(!playing)}
                            >
                                <i className={`fa fa-${playing ? 'pause' : 'play'}`} />
                            </button>
                            <button
                                data-testid="button-fastforward"
                                title={st('Buttons/Fastforward10')}
                                disabled={disabled || !ready}
                                className="btn btn-lg"
                                onClick={() => onSeek(10)}
                            >
                                <i className="fa fa-forward" />
                            </button>
                            <button
                                data-testid="button-recent"
                                title={st('Buttons/Recent')}
                                disabled={disabled || !ready}
                                className="btn btn-lg"
                                onClick={() => {
                                    setCurrent(maxDuration);
                                    player.current.seekTo(maxDuration);
                                }}
                            >
                                <i className="fa fa-fast-forward" />
                            </button>
                            {screenfull.enabled && (
                                <button
                                    data-testid="button-fullscreen"
                                    disabled={!ready}
                                    title={st(`Buttons/${screenfull.isFullscreen ? 'Exit ' : 'Full'}`)}
                                    className="btn btn-lg ml10"
                                    onClick={() => {
                                        if (screenfull.isFullscreen) {
                                            screenfull.exit();
                                        } else {
                                            screenfull.request(playerWrapper.current);
                                        }
                                    }}
                                >
                                    <i className="fa fa-arrows-alt" />
                                </button>
                            )}
                        </div>
                        <div className="text-center mt10">
                            {StringHelpers.displaySeconds(current)}{' '}
                            {isFinite(total) && <span>/ {StringHelpers.displaySeconds(total)}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default e4Player;
